// Shared pagination utilities used by PagePreview and fullscreen overlay
// Computes page-break Y positions for given content using the unified core logic

import {
  PAGE_WIDTH,
  PAGE_HEIGHT,
  getMarginsFromCss,
  buildBlocksFromContainer,
  splitParagraphToFit,
  splitRichParagraphToFit,
  splitTableByRows,
  computeBreaksFromHtml as coreComputeBreaksFromHtml,
} from './paginationCore';

// Compute break Y positions for current ProseMirror DOM
export function computeBreaksFromProseMirror(viewDom) {
  if (!viewDom) return [];

  let cs = null;
  try {
    cs = getComputedStyle(viewDom);
  } catch (_) {
    cs = null;
  }

  const fallbackMargins = { top: 60, right: 60, bottom: 60, left: 60 };
  const margins = cs ? getMarginsFromCss(cs) : fallbackMargins;
  const effectiveMargins = {
    top: Number.isFinite(margins.top) ? margins.top : fallbackMargins.top,
    right: Number.isFinite(margins.right) ? margins.right : fallbackMargins.right,
    bottom: Number.isFinite(margins.bottom) ? margins.bottom : fallbackMargins.bottom,
    left: Number.isFinite(margins.left) ? margins.left : fallbackMargins.left,
  };
  const contentHeight = Math.max(100, PAGE_HEIGHT - effectiveMargins.top - effectiveMargins.bottom);

  // Create hidden measuring container
  const measure = document.createElement('div');
  measure.style.cssText = [
    'position: fixed', 'left: -99999px', 'top: 0', 'width: '+PAGE_WIDTH+'px', 'z-index: -1', 'opacity: 0'
  ].join(';');
  const fakeRoot = document.createElement('div');
  fakeRoot.className = 'tiptap-editor';
  const content = document.createElement('div');
  content.style.cssText = [
    `width: ${PAGE_WIDTH}px`,
    `padding-left: ${effectiveMargins.left}px`, `padding-right: ${effectiveMargins.right}px`,
    `margin-top: ${effectiveMargins.top}px`, `margin-bottom: ${effectiveMargins.bottom}px`,
    'box-sizing: border-box', 'white-space: pre-wrap', 'display: flow-root'
  ].join(';');
  // Важно: используем тот же класс, что и предпросмотр, чтобы применились его CSS-правила
  content.className = 'a4-preview-content ProseMirror';
  if (cs) {
    const fontFamily = cs.fontFamily || 'inherit';
    const fontSize = cs.fontSize || '16px';
    const lineHeight = cs.lineHeight && cs.lineHeight !== 'normal' ? cs.lineHeight : '1.6';
    content.style.fontFamily = fontFamily;
    content.style.fontSize = fontSize;
    content.style.lineHeight = lineHeight;
  }
  fakeRoot.appendChild(content);
  measure.appendChild(fakeRoot);
  document.body.appendChild(measure);

  // Build blocks from current editor DOM
  const parser = document.createElement('div');
  parser.innerHTML = viewDom.innerHTML;
  const blocks = buildBlocksFromContainer(parser);

  const breaks = [];
  let consumedY = 0; // суммарная высота страниц (без верхнего поля)

  const fits = () => content.scrollHeight <= contentHeight;
  const newPage = () => {
    // Разрыв всегда на нижней границе контентной области A4
    breaks.push(consumedY + contentHeight);
    content.innerHTML = '';
    consumedY += contentHeight;
  };

  for (let i = 0; i < blocks.length; i++) {
    const node = blocks[i];
    if (node.getAttribute) {
      const dataType = node.getAttribute('data-type');
      if (dataType === 'page-spacer' || dataType === 'physical-page-break') { newPage(); continue; }
    }

    const clone = node.cloneNode(true);
    content.appendChild(clone);
    if (fits()) continue;

    // overflow: remove and handle
    content.removeChild(clone);
    if (clone.tagName === 'P') {
      // Поддержка как простых, так и «богатых» параграфов
      const res = clone.childElementCount === 0
        ? splitParagraphToFit(clone, content, fits)
        : splitRichParagraphToFit(clone, content, fits);
      if (!res || !res.left) {
        // fallback: перемещаем параграф целиком на новую страницу
        newPage();
        content.appendChild(clone);
        continue;
      }
      content.appendChild(res.left);
      if (!fits()) {
        // nothing fits on this page — force new page
        content.removeChild(res.left);
        newPage();
        content.appendChild(res.left);
      }
      if (res.right && res.right.textContent && res.right.textContent.trim()) {
        newPage();
        content.appendChild(res.right);
      }
    } else if (clone.tagName === 'TABLE') {
      // try to split by rows
      content.appendChild(clone);
      if (fits()) {
        // whole table fits
        continue;
      }
      // overflow: split by rows
      content.removeChild(clone);
      const rest = splitTableByRows(clone, content, fits);
      if (rest) {
        newPage();
        content.appendChild(rest);
      } else {
        // nothing in shell => table too big for a single page; force move
        newPage();
        content.appendChild(clone);
      }
    } else {
      // move to next page
      newPage();
      content.appendChild(clone);
    }
  }

  document.body.removeChild(measure);
  // Переводим координаты из системы "от верха контентной области"
  // в абсолютные относительно верха ProseMirror (с учётом верхнего поля)
  const adjusted = breaks.map(y => effectiveMargins.top + y);
  // Нормализуем: сортировка, удаление дублей и слишком близких линий
  const sorted = Array.from(adjusted).sort((a,b)=>a-b);
  const MIN_SEP = 8; // px — защита от двух подряд линий
  const filtered = [];
  for (const y of sorted) {
    if (!filtered.length || Math.abs(y - filtered[filtered.length-1]) >= MIN_SEP) filtered.push(Math.round(y));
  }
  return filtered;
}

// Compute breaks exactly like PagePreview by using the same HTML and margins marker
export function computeBreaksFromHtml(rawHtml) {
  return coreComputeBreaksFromHtml(rawHtml);
}
const Pagination = { computeBreaksFromProseMirror, computeBreaksFromHtml };
export default Pagination;
